import { DialogContent, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import InfoAlert from 'src/components/InfoAlert';
import { pagesText } from 'src/constants/pagesText';
import { useCreateBasePolicy, useManageBasePolicy } from 'src/hooks/environmentWdacPolicy';
import AddManagedBasePolicyForm from './AddManagedBasePolicyForm';
import UseOtherBasePolicyForm from './UseOtherBasePolicyForm';

export const ADD_BASE_POLICY_VIEW = 'addBasePolicy';
export const USE_OTHER_BASE_POLICY_VIEW = 'useOtherBasePolicy';

export const BASE_POLICY_SUB_VIEW_TITLES = {
  [ADD_BASE_POLICY_VIEW]: { title: 'Add Managed Base Policy', icon: 'mdi:shield-plus-outline' },
  [USE_OTHER_BASE_POLICY_VIEW]: { title: 'Use Other Base Policy', icon: 'mdi:shield-search' },
};

// The two ways a form picking base policies can bring one under management without leaving, each
// setting its description and whether it is the default on the way. Either way the policy is handed
// back through onDone, for the form to return to with it selected.
const BasePolicySubView = ({ view, returnTarget, onDone, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: createBasePolicy, isLoading: adding } = useCreateBasePolicy();
  const { mutateAsync: manageBasePolicy, isLoading: managing } = useManageBasePolicy();

  const handleAdd = async values => {
    try {
      const created = await createBasePolicy(values);
      enqueueSnackbar('Managed base policy added.', { variant: 'success', autoHideDuration: 3000 });
      onDone(created);
    } catch (error) {
      enqueueSnackbar(
        String(error).toLowerCase().includes('already exist')
          ? 'A policy with this ID already exists in this environment.'
          : pagesText.somethingWentWrong,
        { variant: 'error', autoHideDuration: 4000 },
      );
    }
  };

  const handleUseOther = async (policy, settings) => {
    try {
      await manageBasePolicy({ envPolicyId: policy.id, payload: settings });
      enqueueSnackbar(`${policy.policyName} is now a managed base policy.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      onDone(policy);
    } catch (error) {
      // The api layer rejects with the backend's message as a plain string, and the rules behind
      // managing a base policy are worth showing verbatim.
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  return view === ADD_BASE_POLICY_VIEW ? (
    <DialogContent>
      <Stack gap={2}>
        <Typography variant="body2" sx={{ color: '#ccc' }}>
          {`Manually register a managed base policy for this environment. Saving returns you to the ${returnTarget} with the new policy selected.`}
        </Typography>
        <InfoAlert sx={{ py: 0.25 }}>
          <Typography variant="caption">
            Registering a base policy here tells us it exists in your organization so components can
            be generated against it. It does not give us a way to deploy the base policy itself,
            which stays with whatever already distributes it.
          </Typography>
        </InfoAlert>
        <AddManagedBasePolicyForm loading={adding} onCancel={onCancel} onSubmit={handleAdd} />
      </Stack>
    </DialogContent>
  ) : (
    <DialogContent>
      <Stack gap={2}>
        <Typography variant="body2" sx={{ color: '#ccc' }}>
          {`Bring a base policy your devices already report under portal management. Saving returns you to the ${returnTarget} with it selected.`}
        </Typography>
        <UseOtherBasePolicyForm
          withSettings
          loading={managing}
          submitLabel="Manage and Continue"
          onCancel={onCancel}
          onSubmit={handleUseOther}
        />
      </Stack>
    </DialogContent>
  );
};

export default BasePolicySubView;
